import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Modal, Alert, Button, Table, Spinner, Nav } from 'react-bootstrap';
import { store } from 'store/';
import client from '../../modules/client';

const COUNTRIES = [
  { label: 'United States (US)', value: 'US' },
  { label: 'India (IN)', value: 'IN' },
  { label: 'Canada (CA)', value: 'CA' },
  { label: 'United Kingdom (GB)', value: 'GB' },
  { label: 'Australia (AU)', value: 'AU' },
];

export default class SyncCatalogModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'sheet',
      // sheet sync state
      sheetId: '',
      sheetRange: 'Sheet1!A2:T',
      sheetCountry: '',
      syncLoading: false,
      syncResult: null,
      syncError: null,
      // ai generate state
      aiCountry: 'US',
      aiCount: 25,
      aiLoading: false,
      aiRows: null,
      aiError: null,
      // save state
      saveLoading: false,
      saveResult: null,
      saveError: null,
      // export state
      exportCountry: '',
      exportLoading: false,
      exportError: null,
    };
    this.api = client.create();
  }

  getToken = () => store.getState().auth.token;

  handleSyncSheet = async () => {
    const { sheetId, sheetRange, sheetCountry } = this.state;
    if (!sheetId.trim()) {
      this.setState({ syncError: 'Sheet ID is required' });
      return;
    }
    this.api.setHeader('authorization', `Bearer ${this.getToken()}`);
    this.setState({ syncLoading: true, syncResult: null, syncError: null });
    const { status, data } = await this.api.post('/card-types/sync-catalog', {
      sheetId: sheetId.trim(),
      sheetRange: sheetRange.trim() || 'Sheet1!A2:T',
      ...(sheetCountry ? { country: sheetCountry } : {}),
    });
    if (status === 201) {
      this.setState({ syncLoading: false, syncResult: data.result });
    } else {
      this.setState({ syncLoading: false, syncError: data?.error?.message || 'Sync failed' });
    }
  };

  handleAiGenerate = async () => {
    const { aiCountry, aiCount } = this.state;
    this.api.setHeader('authorization', `Bearer ${this.getToken()}`);
    this.setState({
      aiLoading: true,
      aiRows: null,
      aiError: null,
      saveResult: null,
      saveError: null,
    });
    const { status, data } = await this.api.post('/card-types/ai-generate', {
      country: aiCountry,
      count: Number(aiCount),
    });
    if (status === 201) {
      this.setState({ aiLoading: false, aiRows: data.result });
    } else {
      this.setState({ aiLoading: false, aiError: data?.error?.message || 'Generation failed' });
    }
  };

  handleSaveAi = async () => {
    const { aiRows } = this.state;
    if (!aiRows || !aiRows.length) return;
    this.api.setHeader('authorization', `Bearer ${this.getToken()}`);
    this.setState({ saveLoading: true, saveResult: null, saveError: null });
    const { status, data } = await this.api.post('/card-types/ai-generate/save', { rows: aiRows });
    if (status === 201) {
      this.setState({ saveLoading: false, saveResult: data.result, aiRows: null });
    } else {
      this.setState({ saveLoading: false, saveError: data?.error?.message || 'Save failed' });
    }
  };

  handleExport = async () => {
    const { exportCountry } = this.state;
    this.api.setHeader('authorization', `Bearer ${this.getToken()}`);
    this.setState({ exportLoading: true, exportError: null });
    const { status, data } = await this.api.get(
      `/card-types/export-catalog${exportCountry ? `?country=${exportCountry}` : ''}`,
    );
    if (status === 200 && typeof data === 'string') {
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalog-export${exportCountry ? `-${exportCountry}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.setState({ exportLoading: false });
    } else {
      this.setState({ exportLoading: false, exportError: data?.error?.message || 'Export failed' });
    }
  };

  handleClose = () => {
    this.setState({
      sheetId: '',
      sheetRange: 'Sheet1!A2:T',
      sheetCountry: '',
      syncLoading: false,
      syncResult: null,
      syncError: null,
      aiCountry: 'US',
      aiCount: 25,
      aiLoading: false,
      aiRows: null,
      aiError: null,
      saveLoading: false,
      saveResult: null,
      saveError: null,
      exportCountry: '',
      exportLoading: false,
      exportError: null,
      activeTab: 'sheet',
    });
    this.props.onClose();
  };

  renderSheetTab() {
    const { sheetId, sheetRange, sheetCountry, syncLoading, syncResult, syncError } = this.state;
    return (
      <>
        {syncError && (
          <Alert variant="danger" onClose={() => this.setState({ syncError: null })} dismissible>
            {syncError}
          </Alert>
        )}
        {syncResult && (
          <Alert variant="success" onClose={() => this.setState({ syncResult: null })} dismissible>
            Sync complete — upserted: <strong>{syncResult.upserted}</strong>, deleted:{' '}
            <strong>{syncResult.deleted}</strong>, skipped: <strong>{syncResult.skipped}</strong>
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Google Sheet ID <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
            value={sheetId}
            onChange={e => this.setState({ sheetId: e.target.value })}
          />
          <Form.Text className="text-muted">
            Found in the Google Sheet URL: /spreadsheets/d/<strong>SHEET_ID</strong>/edit
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Sheet Range</Form.Label>
          <Form.Control
            type="text"
            value={sheetRange}
            onChange={e => this.setState({ sheetRange: e.target.value })}
          />
          <Form.Text className="text-muted">
            Default: Sheet1!A2:T (skip header row, all 20 columns)
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Filter by Country (optional)</Form.Label>
          <Form.Control
            as="select"
            value={sheetCountry}
            onChange={e => this.setState({ sheetCountry: e.target.value })}
          >
            <option value="">All countries in sheet</option>
            {COUNTRIES.map(c => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <div className="text-muted mb-3" style={{ fontSize: 12 }}>
          <strong>Sheet columns (A→T):</strong> country · bank_name · card_name ·
          plaid_institution_name · image_url · reward_title · reward_rate · reward_type ·
          reward_description · cat_1..cat_10 · action
        </div>

        <Button
          variant="primary"
          onClick={this.handleSyncSheet}
          disabled={syncLoading || !sheetId.trim()}
          className="w-100"
        >
          {syncLoading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" /> Syncing…
            </>
          ) : (
            'Sync from Sheet'
          )}
        </Button>
      </>
    );
  }

  renderAiTab() {
    const {
      aiCountry,
      aiCount,
      aiLoading,
      aiRows,
      aiError,
      saveLoading,
      saveResult,
      saveError,
    } = this.state;
    return (
      <>
        {aiError && (
          <Alert variant="danger" onClose={() => this.setState({ aiError: null })} dismissible>
            {aiError}
          </Alert>
        )}
        {saveError && (
          <Alert variant="danger" onClose={() => this.setState({ saveError: null })} dismissible>
            {saveError}
          </Alert>
        )}
        {saveResult && (
          <Alert variant="success" onClose={() => this.setState({ saveResult: null })} dismissible>
            Saved — upserted: <strong>{saveResult.upserted}</strong>, skipped:{' '}
            <strong>{saveResult.skipped}</strong>
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Country</Form.Label>
              <Form.Control
                as="select"
                value={aiCountry}
                onChange={e => this.setState({ aiCountry: e.target.value, aiRows: null })}
              >
                {COUNTRIES.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Top N cards to generate: <strong>{aiCount}</strong>
              </Form.Label>
              <Form.Control
                type="range"
                min={5}
                max={50}
                step={5}
                value={aiCount}
                onChange={e => this.setState({ aiCount: e.target.value, aiRows: null })}
              />
              <Form.Text className="text-muted">Each card has multiple reward tier rows</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Button
          variant="outline-primary"
          onClick={this.handleAiGenerate}
          disabled={aiLoading}
          className="w-100 mb-3"
        >
          {aiLoading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" /> Generating with Gemini AI…
            </>
          ) : (
            'Generate Preview'
          )}
        </Button>

        {aiRows && aiRows.length > 0 && (
          <>
            <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
              Preview: <strong>{aiRows.length}</strong> reward rows for{' '}
              {aiRows.length > 0 ? aiRows[0].country : aiCountry}. Review below, then click Save to
              write to Firestore.
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              <Table size="sm" bordered striped hover>
                <thead>
                  <tr>
                    <th>Card</th>
                    <th>Bank</th>
                    <th>Reward</th>
                    <th>Rate</th>
                    <th>Categories</th>
                  </tr>
                </thead>
                <tbody>
                  {aiRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 11 }}>{r.cardName}</td>
                      <td style={{ fontSize: 11 }}>{r.bankName}</td>
                      <td style={{ fontSize: 11 }}>{r.rewardTitle}</td>
                      <td style={{ fontSize: 11 }}>
                        {r.rewardRate != null ? `${r.rewardRate}%` : '—'}
                      </td>
                      <td style={{ fontSize: 11 }}>
                        {(r.categories || []).slice(0, 3).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <Button
              variant="success"
              onClick={this.handleSaveAi}
              disabled={saveLoading}
              className="w-100 mt-2"
            >
              {saveLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" /> Saving to Firestore…
                </>
              ) : (
                `Approve & Save ${aiRows.length} rows`
              )}
            </Button>
          </>
        )}
      </>
    );
  }

  renderExportTab() {
    const { exportCountry, exportLoading, exportError } = this.state;
    return (
      <>
        {exportError && (
          <Alert variant="danger" onClose={() => this.setState({ exportError: null })} dismissible>
            {exportError}
          </Alert>
        )}

        <Alert variant="info" className="mb-3" style={{ fontSize: 13 }}>
          Downloads your existing Firestore card catalog as a CSV file in the same format as the
          Google Sheet template. Use this to seed the sheet with what you already have, then edit
          and re-sync.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Filter by Country (optional)</Form.Label>
          <Form.Control
            as="select"
            value={exportCountry}
            onChange={e => this.setState({ exportCountry: e.target.value })}
          >
            <option value="">All countries</option>
            {COUNTRIES.map(c => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <div className="text-muted mb-3" style={{ fontSize: 12 }}>
          <strong>Output columns:</strong> country · bank_name · card_name · plaid_institution_name
          · image_url · reward_title · reward_rate · reward_type · reward_description ·
          cat_1..cat_10 · action
        </div>

        <Button
          variant="outline-success"
          onClick={this.handleExport}
          disabled={exportLoading}
          className="w-100"
        >
          {exportLoading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" /> Exporting…
            </>
          ) : (
            'Download CSV'
          )}
        </Button>
      </>
    );
  }

  render() {
    const { show } = this.props;
    const { activeTab } = this.state;

    return (
      <Modal show={show} onHide={this.handleClose} size="lg" centered>
        <Modal.Header closeButton style={{ padding: '25px 25px 10px 25px' }}>
          <h3 className="modal-header-title-30mrg">Sync Catalog</h3>
        </Modal.Header>
        <Modal.Body>
          <Nav
            variant="tabs"
            className="mb-4"
            activeKey={activeTab}
            onSelect={tab => this.setState({ activeTab: tab })}
          >
            <Nav.Item>
              <Nav.Link eventKey="sheet">Google Sheet</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="ai">AI Generate (Gemini)</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="export">Export Existing</Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'sheet' && this.renderSheetTab()}
          {activeTab === 'ai' && this.renderAiTab()}
          {activeTab === 'export' && this.renderExportTab()}
        </Modal.Body>
      </Modal>
    );
  }
}

SyncCatalogModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

SyncCatalogModal.defaultProps = {
  show: false,
  onClose: null,
};
